import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import {
  boardSwitcherSharedState,
  keyboardNavSharedState,
} from '@trello/board-switcher';
import type { BoardSwitcherSection } from '@trello/board-switcher';
import { useSharedState, useSharedStateSelector } from '@trello/shared-state';

import { BoardListTile } from 'app/src/components/BoardTile/BoardListTile';
import { NewBoardTileLarge } from 'app/src/components/BoardTile/NewBoardTileLarge';
import { focusBoardSwitcherNavItem } from './focusBoardSwitcherNavItem';
import { getBoardSwitcherSource } from './getBoardSwitcherSource';
import { useOpenBoardAndHideSwitcher } from './useOpenBoardAndHideSwitcher';
import { useSetActiveBoardTileOnHoverOrFocus } from './useSetActiveBoardTileOnHoverOrFocus';

import * as styles from './BoardSwitcherTile.module.less';

interface BoardSwitcherTileProps {
  boardId: string;
  section: BoardSwitcherSection;
  className?: string;
  isForcedActive?: boolean;
}

export const BoardSwitcherTile: FunctionComponent<BoardSwitcherTileProps> = ({
  boardId,
  section,
  className,
  isForcedActive,
}) => {
  const openBoardAndHideSwitcher = useOpenBoardAndHideSwitcher();

  const [{ activeId, activeSection }] = useSharedState(keyboardNavSharedState);
  const layout = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((value) => value.layout, []),
  );

  const activeStateHandlers = useSetActiveBoardTileOnHoverOrFocus(
    section,
    boardId,
  );

  const isActive = activeSection === section && activeId === boardId;
  const ariaRole = layout === 'list' ? 'listitem' : 'gridcell';
  const TileComponent = layout === 'list' ? BoardListTile : NewBoardTileLarge;

  useEffect(() => {
    if (isActive) {
      focusBoardSwitcherNavItem(section, boardId);
    }
  }, [boardId, isActive, section]);

  const onClick = () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'boardSwitcherTileLink',
      source: getBoardSwitcherSource(),
      attributes: {
        section,
      },
      containers: formatContainers({
        boardId,
      }),
    });

    openBoardAndHideSwitcher();
  };

  return (
    <TileComponent
      boardId={boardId}
      draggable={false}
      onBoardClick={onClick}
      {...activeStateHandlers}
      className={cx(className, {
        [styles.boardSwitcherListTile]: layout === 'list',
        [styles.boardSwitcherTile]: layout === 'grid',
        [styles.activeBoardTile]: isActive || isForcedActive,
      })}
      data-nav-item="board-tile"
      data-id={boardId}
      data-section={section}
      role={ariaRole}
    />
  );
};
